import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import axios from "axios";
import {
  Save,
  Landmark
} from "lucide-react";
import * as ownerApi from "../../api/payroll/staffBankAccountApi";
import * as portalApi from "../../api/payroll/staffBankAccountPortalApi";
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const http = axios.create({ baseURL: API, withCredentials: true });

const ACCOUNT_TYPE_OPTIONS = [
  { label: "Savings", value: "SAVINGS" },
  { label: "Current", value: "CURRENT" },
];

export default function StaffBankAccountForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/bank-accounts" : "/bank-accounts";
  const navigate = useNavigate();
  const { id } = useParams();
  const [staffList, setStaffList] = useState([]);
  const [form, setForm] = useState({
    staffId: "",
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    branchName: "",
    accountType: "SAVINGS",
    isDefault: true,
  });
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await http.get(isPortal ? "/staff-portal/all" : "/staff/all");
        const data = res.data;
        setStaffList(Array.isArray(data) ? data : data.data || data.staff || []);
      } catch {
        try {
          const res2 = await http.get("/staff");
          const d = res2.data;
          setStaffList(Array.isArray(d) ? d : d.data || []);
        } catch {
          /* ignore */
        }
      }
    })();
  }, [isPortal]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.getAllBankAccounts();
        const list = Array.isArray(res) ? res : res?.data || [];
        const found = list.find((x) => x._id === id);
        if (found) {
          setForm({
            staffId: found.staffId?._id || found.staffId || "",
            accountHolderName: found.accountHolderName || "",
            bankName: found.bankName || "",
            accountNumber: found.accountNumber || "",
            ifscCode: found.ifscCode || "",
            branchName: found.branchName || "",
            accountType: found.accountType || "SAVINGS",
            isDefault: found.isDefault !== false,
          });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Load failed");
      }
    })();
  }, [id, isPortal, api]);

  const submit = async (e) => {
    e.preventDefault();
    // NOTE: the Staff field used to be a native <select required>, which gave
    // free browser validation. Swapping it for the custom <Select> (needed
    // for consistent styling) means that validation no longer fires on its
    // own, so it's re-implemented here to keep the same "can't save without
    // a staff member" behavior.
    if (!form.staffId) {
      toast.error("Staff is required");
      return;
    }
    setLoading(true);
    try {
      await api.upsertBankAccount(form);
      navigate(base);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const staffOptions = [
    { label: "Select staff", value: "" },
    ...staffList.map((s) => ({ label: `${s.name} (${s.staffCode})`, value: s._id })),
  ];

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4 sm:p-6">
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          <Landmark className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          {id ? "Edit" : "Add"} Bank Account
        </h1>
      </div>

      

      <Card>
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <Select
              label="Staff"
              options={staffOptions}
              value={form.staffId}
              onChange={(val) => setForm({ ...form, staffId: val })}
            />

            <Input
              label="Account holder name"
              value={form.accountHolderName}
              onChange={(e) => setForm({ ...form, accountHolderName: e.target.value })}
              required
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Bank name"
                value={form.bankName}
                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                required
              />

              <Input
                label="Branch"
                value={form.branchName}
                onChange={(e) => setForm({ ...form, branchName: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Account number"
                value={form.accountNumber}
                onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
                required
              />

              <Input
                label="IFSC"
                value={form.ifscCode}
                onChange={(e) => setForm({ ...form, ifscCode: e.target.value.toUpperCase() })}
                required
              />
            </div>

            <Select
              label="Account type"
              options={ACCOUNT_TYPE_OPTIONS}
              value={form.accountType}
              onChange={(val) => setForm({ ...form, accountType: val })}
            />

            <Checkbox
              checked={form.isDefault}
              onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
              label="Default account"
            />

            <div className="flex gap-3 pt-2">
              <Button type="submit" variant="primary" isLoading={loading} leftIcon={<Save className="h-4 w-4" />}>
                Save
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate(base)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}